/* organization/Branding — White-label branding with LIVE PREVIEW, reset, and apply.
   Per-org branding fetched from API on mount. Tabs: General, Colors & Fonts, Email Branding, White Label. */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, TabBar, TenancyModal } from '../platform/_ui';
import { defaultBranding } from '../platform/platformStore';
import * as brandingApi from '../api/branding.api.js';
import { applyBrandingTokens } from '../shared/branding/index.js';
import { useOrgRuntime } from './OrganizationRuntimeContext.jsx';
import { usePermissions } from './auth/usePermissions.js';
import { DeniedState } from './auth/DeniedState.jsx';

const SWATCHES  = ['#125AB0', '#0E8C5A', '#6E3FBA', '#C26A1F', '#A8508A', '#0D4281'];
const SECONDARY = ['#E5F0FF', '#E8F8F0', '#F0EAFE', '#FFF3E0', '#FDE8F5', '#E3ECF5'];
const ACCENT    = ['#4692EC', '#00A878', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899'];
const FONTS     = ['Inter', 'Roboto', 'DM Sans', 'Plus Jakarta Sans', 'Nunito', 'Lato', 'Open Sans', 'Poppins'];

const DOMAIN_STATUS_TONE  = { VERIFIED: 'green', PENDING: 'amber', UNVERIFIED: 'gray' };
const DOMAIN_STATUS_LABEL = { VERIFIED: 'Verified', PENDING: 'Pending Verification', UNVERIFIED: 'Unverified' };

const BRAND_TABS = [
  { id: 'general',    label: 'General',        icon: 'Eye'      },
  { id: 'advanced',   label: 'Colors & Fonts',  icon: 'Palette'  },
  { id: 'email',      label: 'Email Branding',  icon: 'Mail'     },
  { id: 'whitelabel', label: 'White Label',     icon: 'Layers'   },
];

const WL_EMPTY = { name: '', logoUrl: '', primaryColor: '', secondaryColor: '', welcomeMessage: '', footerText: '', supportEmail: '' };

function ColorRow({ label, value, onChange, swatches }) {
  return (
    <div className="nx-form-row">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {swatches.map(c => (
          <button key={c} onClick={() => onChange(c)} title={c}
            className={'nx-brand-swatch' + (value === c ? ' is-active' : '')}
            style={{ background: c }} />
        ))}
        <input type="color" value={value || '#125AB0'} onChange={e => onChange(e.target.value)} className="nx-brand-color-input" />
        <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{value}</span>
      </div>
    </div>
  );
}

export default function Branding({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const toast = useToast();
  const org = data.state.organizations.find(o => o.id === orgId);
  const [draft, setDraft] = useState(() => data.state.branding?.[orgId] || defaultBranding(org));
  const [tab, setTab] = useState('general');
  const [domainStatus, setDomainStatus]     = useState(null);
  const [logoPreview, setLogoPreview]           = useState(null);
  const [darkLogoPreview, setDarkLogoPreview]   = useState(null);
  const [faviconPreview, setFaviconPreview]     = useState(null);
  const [selectedWsId, setSelectedWsId]     = useState('');
  const [wlDraft, setWlDraft]               = useState(WL_EMPTY);
  const [wlHasRecord, setWlHasRecord]       = useState(false);
  const [wlSaving, setWlSaving]             = useState(false);
  const [wlResetting, setWlResetting]       = useState(false);
  const [wlUploading, setWlUploading]       = useState(false);
  const [verifying, setVerifying]           = useState(false);
  const [confirmReset, setConfirmReset]     = useState(false);
  const [testingEmail, setTestingEmail]     = useState(false);
  const [wsOverride, setWsOverride]         = useState([]);

  const load = useCallback(async () => {
    try {
      const b = await brandingApi.getOrgBranding(orgId);
      if (b) {
        setDraft(d => ({
          ...d,
          primaryColor:   b.primaryColor || d.primaryColor,
          secondary:      b.secondaryColor|| b.secondary      || d.secondary,
          accentColor:    b.accentColor                       || d.accentColor,
          successColor:   b.successColor                      || d.successColor,
          warningColor:   b.warningColor                      || d.warningColor,
          dangerColor:    b.dangerColor                       || d.dangerColor,
          font:           b.font                              || d.font,
          logoText:       b.logoText      || b.name           || d.logoText,
          favicon:        b.favicon                          || d.favicon,
          loginHeadline:  b.loginHeadline                    || d.loginHeadline,
          footer:         b.footer                           || d.footer,
          domain:         b.customDomain  || b.domain        || d.domain,
          supportEmail:   b.supportEmail                     || d.supportEmail || '',
          emailFromName:  b.emailFromName                    || d.emailFromName,
          emailFromAddr:  b.emailFromAddr                    || d.emailFromAddr,
          emailSignature: b.emailSignature                   || d.emailSignature,
          emailFooter:    b.emailFooter                      || d.emailFooter,
          logoUrl:        b.logoUrl                          || d.logoUrl,
          darkLogoUrl:    b.darkLogoUrl                      || d.darkLogoUrl,
          faviconUrl:     b.faviconUrl                       || d.faviconUrl,
        }));
        if (b.domainStatus) setDomainStatus(b.domainStatus);
        if (b.logoUrl)     setLogoPreview(b.logoUrl);
        if (b.darkLogoUrl) setDarkLogoPreview(b.darkLogoUrl);
        if (b.faviconUrl)  setFaviconPreview(b.faviconUrl);
      }
    } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load branding data' }); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  /* Load workspaces from API when the store hasn't populated this org's list yet */
  useEffect(() => {
    const stored = (data.state.workspaces || []).filter(w => w.org === orgId || w.orgId === orgId);
    if (stored.length > 0) return;
    const api = window.__nx_api;
    if (!api?.workspaces?.listByOrg) return;
    api.workspaces.listByOrg(orgId, { pageSize: 100 })
      .then(r => setWsOverride(r?.data ?? r?.workspaces ?? []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadWl = useCallback(async (wsId) => {
    if (!wsId) return;
    try {
      const wl = await brandingApi.getWhiteLabel(wsId);
      // id present = real DB record; absent = WL_DEFAULTS placeholder (no override)
      const hasRecord = Boolean(wl && Object.prototype.hasOwnProperty.call(wl, 'id'));
      setWlHasRecord(hasRecord);
      if (!hasRecord) {
        // No override exists — show blank form so admin only fills what they want to override.
        // Blank fields inherit org branding via resolved-branding field-level fallthrough.
        setWlDraft(WL_EMPTY);
      } else {
        setWlDraft({
          name:           wl?.brand          || '',
          logoUrl:        wl?.faviconUrl     || '',
          primaryColor:   wl?.primaryColor   || '',
          secondaryColor: wl?.secondaryColor || '',
          welcomeMessage: wl?.welcomeMessage || '',
          footerText:     wl?.footerText     || '',
          supportEmail:   wl?.supportEmail   || '',
        });
      }
    } catch {
      setWlHasRecord(false);
      setWlDraft(WL_EMPTY);
    }
  }, []);

  const perm = usePermissions();
  const { refreshRuntime, patchBranding } = useOrgRuntime();

  /* Bug 2 fix: ref always points to the current saved branding so the live-preview
     cleanup never reads a stale closure after Apply is clicked. Updated each render
     (render-phase assignment is intentional — refs don't trigger re-renders). */
  const savedBrandingRef = React.useRef(null);

  /* Live preview — apply draft colors to CSS vars while on Colors & Fonts tab.
     Reverts to saved branding when leaving the tab or unmounting. */
  useEffect(() => {
    if (tab !== 'advanced') return;
    applyBrandingTokens({
      primaryColor:   draft.primaryColor || '#125AB0',
      accentColor:    draft.accentColor,
      secondaryColor: draft.secondary,
      successColor:   draft.successColor,
      warningColor:   draft.warningColor,
      dangerColor:    draft.dangerColor,
      font:           draft.font,
    });
    if (draft.font) {
      let fontLink = document.getElementById('nx-brand-font');
      if (!fontLink) {
        fontLink = document.createElement('link');
        fontLink.id  = 'nx-brand-font';
        fontLink.rel = 'stylesheet';
        document.head.appendChild(fontLink);
      }
      fontLink.href = `https://fonts.googleapis.com/css2?family=${draft.font.replaceAll(' ', '+')}:wght@400;500;600;700;800&display=swap`;
    }

    /* Revert to saved branding when leaving this tab.
       Read from savedBrandingRef (updated each render) — not the stale closure
       over data.state — so the revert reflects values set by Apply. */
    return () => {
      const saved = savedBrandingRef.current || defaultBranding(org);
      applyBrandingTokens({
        primaryColor:   saved.primaryColor || '#125AB0',
        accentColor:    saved.accentColor,
        secondaryColor: saved.secondary    || saved.secondaryColor,
        successColor:   saved.successColor,
        warningColor:   saved.warningColor,
        dangerColor:    saved.dangerColor,
        font:           saved.font,
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, draft.primaryColor, draft.secondary, draft.accentColor, draft.successColor, draft.warningColor, draft.dangerColor, draft.font]);

  if (!org) return null;

  const isAdmin = perm.has('org.branding.view') || perm.isSA;

  if (!isAdmin) return (
    <div className="nx-cc-page">
      <DeniedState
        reason="insufficient_permission"
        message="Branding configuration requires the org.branding.view capability. Contact your org admin to request access."
      />
    </div>
  );

  const storedWorkspaces = (data.state.workspaces || []).filter(w => w.org === orgId || w.orgId === orgId);
  const orgWorkspaces    = storedWorkspaces.length > 0 ? storedWorkspaces : wsOverride;
  const savedSnapshot    = data.state.branding?.[orgId] || defaultBranding(org);
  savedBrandingRef.current = savedSnapshot; /* always-current — cleanup reads this, not stale closure */
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(savedSnapshot);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Only images allowed', desc: 'Please upload a PNG, JPG, SVG, or similar image file.' });
      e.target.value = '';
      return;
    }
    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);
    try {
      const { url } = await brandingApi.uploadOrgBrandingAsset(orgId, file);
      set('logoUrl', url);
      setLogoPreview(url);
      URL.revokeObjectURL(preview);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Logo upload failed', desc: err.message });
      set('logoUrl', '');
      setLogoPreview(null);
      URL.revokeObjectURL(preview);
    }
    e.target.value = '';
  };

  const handleFavicon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Only images allowed', desc: 'Please upload an image file for favicon.' });
      e.target.value = '';
      return;
    }
    const preview = URL.createObjectURL(file);
    setFaviconPreview(preview);
    try {
      const { url } = await brandingApi.uploadOrgBrandingAsset(orgId, file);
      set('faviconUrl', url);
      setFaviconPreview(url);
      URL.revokeObjectURL(preview);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Favicon upload failed', desc: err.message });
      set('faviconUrl', '');
      setFaviconPreview(null);
      URL.revokeObjectURL(preview);
    }
    e.target.value = '';
  };

  const handleDarkLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Only images allowed', desc: 'Please upload a PNG, JPG, SVG, or similar image file.' });
      e.target.value = '';
      return;
    }
    const preview = URL.createObjectURL(file);
    setDarkLogoPreview(preview);
    try {
      const { url } = await brandingApi.uploadOrgBrandingAsset(orgId, file);
      set('darkLogoUrl', url);
      setDarkLogoPreview(url);
      URL.revokeObjectURL(preview);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Dark logo upload failed', desc: err.message });
      set('darkLogoUrl', '');
      setDarkLogoPreview(null);
      URL.revokeObjectURL(preview);
    }
    e.target.value = '';
  };

  const handleWlLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file?.type.startsWith('image/')) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Only images allowed' });
      e.target.value = '';
      return;
    }
    const preview = URL.createObjectURL(file);
    setWlDraft(d => ({ ...d, logoUrl: preview }));
    setWlUploading(true);
    try {
      const { url } = await brandingApi.uploadWhiteLabelAsset(selectedWsId, file);
      setWlDraft(d => ({ ...d, logoUrl: url }));
      URL.revokeObjectURL(preview);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Logo upload failed', desc: err.message });
      setWlDraft(d => ({ ...d, logoUrl: '' }));
      URL.revokeObjectURL(preview);
    } finally {
      setWlUploading(false);
      e.target.value = '';
    }
  };

  const handleWsChange = (wsId) => {
    setSelectedWsId(wsId);
    loadWl(wsId);
  };

  const apply = async () => {
    try {
      await brandingApi.updateOrgBranding(orgId, {
        primaryColor:   draft.primaryColor,
        secondaryColor: draft.secondary,
        accentColor:    draft.accentColor,
        successColor:   draft.successColor,
        warningColor:   draft.warningColor,
        dangerColor:    draft.dangerColor,
        logoText:       draft.logoText,
        favicon:        draft.favicon,
        font:           draft.font,
        loginHeadline:  draft.loginHeadline,
        footer:         draft.footer,
        domain:         draft.domain,
        supportEmail:   draft.supportEmail,
        emailFromName:  draft.emailFromName,
        emailFromAddr:  draft.emailFromAddr,
        emailSignature: draft.emailSignature,
        emailFooter:    draft.emailFooter,
        ...(draft.logoUrl     && !draft.logoUrl.startsWith('blob:')     ? { logoUrl:     draft.logoUrl     } : {}),
        ...(draft.darkLogoUrl && !draft.darkLogoUrl.startsWith('blob:') ? { darkLogoUrl: draft.darkLogoUrl } : {}),
        ...(draft.faviconUrl  && !draft.faviconUrl.startsWith('blob:')  ? { faviconUrl:  draft.faviconUrl  } : {}),
      });
      data.update(s => ({
        ...s,
        branding: { ...s.branding, [orgId]: { ...draft } },
      }));
      data.addAudit({ action: 'updated branding', target: org.name, cat: 'Org' });
      /* Optimistic patch — OrganizationLayout's useLayoutEffect fires synchronously
         on next render, applying CSS vars before the browser paints. No refresh needed. */
      patchBranding({
        primaryColor:   draft.primaryColor      || null,
        secondaryColor: draft.secondary    || null,
        accentColor:    draft.accentColor  || null,
        successColor:   draft.successColor || null,
        warningColor:   draft.warningColor || null,
        dangerColor:    draft.dangerColor  || null,
        font:           draft.font         || null,
        logoText:       draft.logoText     || null,
        ...(draft.logoUrl   && !draft.logoUrl.startsWith('blob:')   && { logoUrl:   draft.logoUrl   }),
        ...(draft.faviconUrl && !draft.faviconUrl.startsWith('blob:') && { faviconUrl: draft.faviconUrl }),
      });
      toast({ tone: 'success', icon: 'Check', title: 'Branding Applied', desc: org.name });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to apply branding', desc: err.message || 'Server error' });
    }
  };

  const reset = async () => {
    setConfirmReset(false);
    try {
      await brandingApi.resetOrgBranding(orgId);
      const def = defaultBranding(org);
      setDraft(def);
      setLogoPreview(null);
      setDarkLogoPreview(null);
      setFaviconPreview(null);
      setDomainStatus(null);
      data.update(s => { const b = { ...s.branding }; delete b[orgId]; return { ...s, branding: b }; });
      refreshRuntime();
      toast({ tone: 'warning', icon: 'RotateCcw', title: 'Branding Reset to Default' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to reset branding', desc: err.message || 'Server error' });
    }
  };

  const verifyDomain = async () => {
    if (!draft.domain) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'No domain configured', desc: 'Enter a custom domain before verifying.' });
      return;
    }
    setVerifying(true);
    try {
      const result = await brandingApi.verifyDomain(orgId);
      setDomainStatus(result.domainStatus);
      toast({ tone: 'info', icon: 'Globe', title: 'Domain Verification Initiated', desc: 'DNS validation is in progress. Check back shortly.' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Verification failed', desc: err.message || 'Server error' });
    } finally {
      setVerifying(false);
    }
  };

  const saveWl = async () => {
    if (!selectedWsId) return;
    setWlSaving(true);
    try {
      await brandingApi.updateWhiteLabel(selectedWsId, {
        brand:          wlDraft.name          || undefined,
        primaryColor:   wlDraft.primaryColor  || undefined,
        secondaryColor: wlDraft.secondaryColor || undefined,
        welcomeMessage: wlDraft.welcomeMessage || undefined,
        footerText:     wlDraft.footerText    || undefined,
        supportEmail:   wlDraft.supportEmail  || undefined,
        ...(wlDraft.logoUrl && !wlDraft.logoUrl.startsWith('blob:') ? {
          faviconUrl: wlDraft.logoUrl,
          logo:       true,
          favicon:    true,
        } : {}),
      });
      setWlHasRecord(true);
      toast({ tone: 'success', icon: 'Check', title: 'White-label settings saved' });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save white-label', desc: e.message });
    } finally {
      setWlSaving(false);
    }
  };

  const resetWl = async () => {
    if (!selectedWsId) return;
    setWlResetting(true);
    try {
      await brandingApi.resetWhiteLabel(selectedWsId);
      setWlHasRecord(false);
      setWlDraft(WL_EMPTY);
      toast({ tone: 'warning', icon: 'RotateCcw', title: 'White label reset', desc: 'Workspace will now inherit org branding.' });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to reset white label', desc: e.message });
    } finally {
      setWlResetting(false);
    }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      await brandingApi.testOrgBrandingEmail(orgId);
      toast({ tone: 'success', icon: 'Mail', title: 'Test email sent', desc: 'Check your inbox to review the branded email.' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to send test email', desc: err.message || 'Server error or email not configured.' });
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Branding"
        sub={'White-label settings for ' + org.name}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={<>
          <Btn variant="ghost" icon="RotateCcw" onClick={() => setConfirmReset(true)}>Reset</Btn>
          <Btn variant="primary" icon="Check" onClick={apply} disabled={!dirty}>Apply Branding</Btn>
        </>}
      />

      <TabBar tabs={BRAND_TABS} active={tab} onChange={setTab} />

      {tab === 'general' && (
        <div className="nx-org-brand-grid">
          {/* Controls */}
          <div className="nx-panel" style={{ padding: 20 }}>
            <h3 className="nx-panel-title" style={{ marginBottom: 14 }}>General Settings</h3>
            <div className="nx-form">

              {/* Logo upload */}
              <div className="nx-form-row">
                <label>Logo</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input type="file" accept="image/*" onChange={handleLogo} className="nx-input" style={{ padding: '4px 8px' }} />
                  {logoPreview && (
                    <img src={logoPreview} alt="Logo preview" style={{ height: 40, maxWidth: 160, objectFit: 'contain', borderRadius: 4, border: '1px solid var(--nx-border)', background: 'var(--nx-bg-alt,#f6f8fa)', padding: 4 }} />
                  )}
                </div>
              </div>

              <div className="nx-form-row"><label>Logo Text (Light)</label><input className="nx-input" value={draft.logoText || ''} onChange={e => set('logoText', e.target.value)} placeholder="Brand name" /></div>

              {/* Favicon upload */}
              <div className="nx-form-row">
                <label>Favicon</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input type="file" accept="image/*" onChange={handleFavicon} className="nx-input" style={{ padding: '4px 8px' }} />
                  {faviconPreview && (
                    <img src={faviconPreview} alt="Favicon preview" style={{ height: 32, width: 32, objectFit: 'contain', borderRadius: 4, border: '1px solid var(--nx-border)', background: 'var(--nx-bg-alt,#f6f8fa)', padding: 2 }} />
                  )}
                </div>
              </div>

              <div className="nx-form-row"><label>Favicon Initial</label><input className="nx-input" maxLength={2} value={draft.favicon || ''} onChange={e => set('favicon', e.target.value)} placeholder="A" style={{ maxWidth: 80 }} /></div>
              <ColorRow label="Primary Color" value={draft.primaryColor} onChange={v => set('primaryColor', v)} swatches={SWATCHES} />
              <div className="nx-form-row"><label>Login Headline</label><input className="nx-input" value={draft.loginHeadline || ''} onChange={e => set('loginHeadline', e.target.value)} placeholder="Welcome to…" /></div>
              <div className="nx-form-row"><label>Footer Text</label><input className="nx-input" value={draft.footer || ''} onChange={e => set('footer', e.target.value)} placeholder="© 2026 Company" /></div>

              {/* Support email */}
              <div className="nx-form-row">
                <label>Support Email</label>
                <input className="nx-input" type="email" value={draft.supportEmail || ''} onChange={e => set('supportEmail', e.target.value)} placeholder="support@yourcompany.com" />
              </div>

              {/* Custom domain + status badge */}
              <div className="nx-form-row">
                <label>Custom Domain</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="nx-input" value={draft.domain || ''} onChange={e => set('domain', e.target.value)} placeholder="crm.yourcompany.com" style={{ flex: 1 }} />
                    <Btn variant="secondary" icon="Globe" onClick={verifyDomain} disabled={verifying || !draft.domain}>
                      {verifying ? 'Verifying…' : 'Verify'}
                    </Btn>
                  </div>
                  {domainStatus && (
                    <Pill tone={DOMAIN_STATUS_TONE[domainStatus] || 'gray'}>
                      {DOMAIN_STATUS_LABEL[domainStatus] || domainStatus}
                    </Pill>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Live preview */}
          <div>
            <div className="nx-org-preview-label"><NexusIcon name="Eye" size={13} /> Live Preview{dirty && <span className="nx-org-preview-dirty">Unsaved</span>}</div>
            <div className="nx-org-preview-card" style={{ '--brand': draft.primaryColor }}>
              <div className="nx-org-preview-login">
                <div className="nx-org-preview-logo" style={{ background: draft.primaryColor }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 'inherit' }} />
                    : (draft.favicon || draft.logoText || 'N')[0]}
                </div>
                <div className="nx-org-preview-brandname">{draft.logoText || 'Organization'}</div>
                <div className="nx-org-preview-headline">{draft.loginHeadline || 'Welcome'}</div>
                <div className="nx-org-preview-field" />
                <div className="nx-org-preview-field" />
                <div className="nx-org-preview-btn" style={{ background: draft.primaryColor }}>Sign In</div>
                <div className="nx-org-preview-footer">{draft.footer || '© 2026'}</div>
              </div>
            </div>
            <div className="nx-org-preview-card" style={{ marginTop: 12 }}>
              <div className="nx-org-preview-app">
                <div className="nx-org-preview-appbar" style={{ background: draft.primaryColor }}>
                  <span className="nx-org-preview-appbar-mark">{(draft.favicon || 'N')[0]}</span>
                  <span>{draft.logoText || 'Organization'}</span>
                  <span className="nx-org-preview-domain">{draft.domain || 'app.ursa.com'}</span>
                </div>
                <div className="nx-org-preview-appbody">
                  <div className="nx-org-preview-chip" style={{ background: (draft.primaryColor || '#125AB0') + '22', color: draft.primaryColor }}>Primary</div>
                  <div className="nx-org-preview-chip" style={{ background: (draft.secondary || '#E5F0FF'), color: draft.primaryColor || 'var(--nx-accent, #125AB0)', marginLeft: 4 }}>Secondary</div>
                  <div className="nx-org-preview-line" />
                  <div className="nx-org-preview-line short" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'advanced' && (
        <div className="nx-org-brand-grid">
          <div className="nx-panel" style={{ padding: 20 }}>
            <h3 className="nx-panel-title" style={{ marginBottom: 14 }}>Colors & Typography</h3>
            <div className="nx-form">
              {/* Dark logo upload — same pattern as General tab logo */}
              <div className="nx-form-row">
                <label>Dark Logo</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input type="file" accept="image/*" onChange={handleDarkLogo} className="nx-input" style={{ padding: '4px 8px' }} />
                  {darkLogoPreview && (
                    <img src={darkLogoPreview} alt="Dark logo preview" style={{ height: 40, maxWidth: 160, objectFit: 'contain', borderRadius: 4, border: '1px solid var(--nx-border)', background: '#1A1D23', padding: 4 }} />
                  )}
                </div>
              </div>

              <ColorRow label="Secondary Color" value={draft.secondary || '#E5F0FF'} onChange={v => set('secondary', v)} swatches={SECONDARY} />
              <div style={{ marginLeft: 120, fontSize: 11, color: 'var(--nx-text-faint)', marginTop: -8, marginBottom: 8 }}>Soft tint used for chip backgrounds, hover surfaces, and subtle highlights.</div>

              <ColorRow label="Accent Color"    value={draft.accentColor  || '#4692EC'} onChange={v => set('accentColor',  v)} swatches={ACCENT} />
              <div style={{ marginLeft: 120, fontSize: 11, color: 'var(--nx-text-faint)', marginTop: -8, marginBottom: 8 }}>Interactive elements — buttons, links, tabs, focus rings, and selection highlights.</div>

              <ColorRow label="Success Color"   value={draft.successColor || '#0E8C5A'} onChange={v => set('successColor', v)} swatches={['#0E8C5A', '#10B981', '#22C55E', '#16A34A', '#059669', '#047857']} />
              <div style={{ marginLeft: 120, fontSize: 11, color: 'var(--nx-text-faint)', marginTop: -8, marginBottom: 8 }}>Confirmed, active, and healthy states — success badges, completion indicators.</div>

              <ColorRow label="Warning Color"   value={draft.warningColor || '#B5740E'} onChange={v => set('warningColor', v)} swatches={['#B5740E', '#F59E0B', '#D97706', '#CA8A04', '#A16207', '#92400E']} />
              <div style={{ marginLeft: 120, fontSize: 11, color: 'var(--nx-text-faint)', marginTop: -8, marginBottom: 8 }}>Attention-required states — near-limit alerts, pending actions, trial notices.</div>

              <ColorRow label="Danger Color"    value={draft.dangerColor  || '#DE3217'} onChange={v => set('dangerColor',  v)} swatches={['#DE3217', '#EF4444', '#DC2626', '#B91C1C', '#991B1B', '#7F1D1D']} />
              <div style={{ marginLeft: 120, fontSize: 11, color: 'var(--nx-text-faint)', marginTop: -8, marginBottom: 8 }}>Destructive and critical states — delete buttons, over-limit warnings, error alerts.</div>

              <div className="nx-form-row">
                <label htmlFor="brand-font-select">Brand Font</label>
                <select id="brand-font-select" className="nx-input" value={draft.font || 'Inter'} onChange={e => set('font', e.target.value)}>
                  {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div style={{ marginLeft: 120, fontSize: 11, color: 'var(--nx-text-faint)', marginTop: -8, marginBottom: 8 }}>Applied to the entire application — sidebar, navigation, headers, forms, and tables.</div>
            </div>

            <h3 className="nx-panel-title" style={{ marginTop: 20, marginBottom: 14 }}>Dark Mode Preview</h3>
            <div style={{ background: '#1A1D23', borderRadius: 8, padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: draft.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, overflow: 'hidden', flexShrink: 0 }}>
                {darkLogoPreview
                  ? <img src={darkLogoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : (draft.favicon || draft.logoText || 'N')[0]}
              </div>
              <div>
                <div style={{ fontFamily: draft.font || 'Inter', fontSize: 14, fontWeight: 700, color: '#E6EDF3' }}>{draft.logoText || 'Organization'}</div>
                <div style={{ fontSize: 11, color: '#8B949E', marginTop: 2 }}>{draft.domain || 'app.ursa.com'}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="nx-org-preview-label"><NexusIcon name="Palette" size={13} /> Color Palette</div>
            <div className="nx-panel" style={{ padding: 16 }}>
              {[
                { label: 'Primary',   color: draft.primaryColor      || 'var(--nx-accent, #125AB0)' },
                { label: 'Secondary', color: draft.secondary    || 'var(--nx-accent-tint, #E5F0FF)' },
                { label: 'Accent',    color: draft.accentColor  || 'var(--nx-accent-hover, #4692EC)' },
                { label: 'Success',   color: draft.successColor || '#0E8C5A' },
                { label: 'Warning',   color: draft.warningColor || '#B5740E' },
                { label: 'Danger',    color: draft.dangerColor  || '#DE3217' },
              ].map(({ label, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--nx-border)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: color, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{color}</div>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 6 }}>Typography preview</div>
                <div style={{ fontFamily: draft.font || 'Inter' }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>Heading One</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>Heading Two</div>
                  <div style={{ fontSize: 13, marginTop: 4, color: 'var(--nx-text-muted)' }}>Body text in {draft.font || 'Inter'}. The quick brown fox jumps.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'email' && (
        <div className="nx-org-brand-grid">
          <div className="nx-panel" style={{ padding: 20 }}>
            <h3 className="nx-panel-title" style={{ marginBottom: 14 }}>Email Branding</h3>
            <div className="nx-form">
              <div className="nx-form-row"><label>From Name</label><input className="nx-input" value={draft.emailFromName || ''} onChange={e => set('emailFromName', e.target.value)} placeholder={org.name} /></div>
              <div className="nx-form-row"><label>From Address</label><input className="nx-input" type="email" value={draft.emailFromAddr || ''} onChange={e => set('emailFromAddr', e.target.value)} placeholder={'hello@' + (draft.domain || 'yourcompany.com')} /></div>
              <div className="nx-form-row">
                <label>Email Signature</label>
                <textarea className="nx-input" rows={3} style={{ resize: 'vertical', fontFamily: 'inherit' }}
                  value={draft.emailSignature || ''} onChange={e => set('emailSignature', e.target.value)}
                  placeholder={'Best regards,\nThe ' + org.name + ' Team'} />
              </div>
              <div className="nx-form-row">
                <label>Email Footer</label>
                <textarea className="nx-input" rows={2} style={{ resize: 'vertical', fontFamily: 'inherit' }}
                  value={draft.emailFooter || ''} onChange={e => set('emailFooter', e.target.value)}
                  placeholder={'© 2026 ' + org.name + ' · Unsubscribe'} />
              </div>
            </div>
            <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 6, background: 'var(--nx-border)30', fontSize: 12, color: 'var(--nx-text-muted)' }}>
              <NexusIcon name="Info" size={12} style={{ marginRight: 4 }} />
              Custom from address requires SMTP configuration in Settings → SMTP.
            </div>
            <div style={{ marginTop: 12 }}>
              <Btn variant="secondary" icon="Send" onClick={sendTestEmail} disabled={testingEmail}>
                {testingEmail ? 'Sending…' : 'Send Test Email'}
              </Btn>
            </div>
          </div>

          {/* Email preview */}
          <div>
            <div className="nx-org-preview-label"><NexusIcon name="Mail" size={13} /> Email Preview</div>
            <div style={{ border: '1px solid var(--nx-border)', borderRadius: 8, overflow: 'hidden', background: '#fff', fontFamily: draft.font || 'Inter' }}>
              <div style={{ background: draft.primaryColor || 'var(--nx-accent, #125AB0)', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 6, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                    : (draft.favicon || draft.logoText || 'N')[0]}
                </div>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{draft.logoText || org.name}</span>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#111', marginBottom: 12 }}>You've been invited</div>
                <div style={{ fontSize: 14, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
                  Hi there,<br /><br />
                  You have been invited to join <strong>{org.name}</strong> on the URSA platform.
                </div>
                <div style={{ background: draft.primaryColor || 'var(--nx-accent, #125AB0)', color: '#fff', padding: '10px 20px', borderRadius: 6, display: 'inline-block', fontSize: 13, fontWeight: 600 }}>Accept Invitation</div>
                {draft.emailSignature && <div style={{ marginTop: 20, fontSize: 13, color: '#555', whiteSpace: 'pre-line' }}>{draft.emailSignature}</div>}
              </div>
              <div style={{ borderTop: '1px solid #eee', padding: '12px 24px', background: '#f8f9fa' }}>
                <div style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>
                  {draft.emailFooter || '© 2026 ' + org.name}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'whitelabel' && (
        <div style={{ maxWidth: 700 }}>
          <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 6, background: 'var(--nx-accent, #125AB0)12', border: '1px solid var(--nx-accent, #125AB0)30', fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <NexusIcon name="Info" size={13} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>
              Each workspace inherits org branding by default. Override specific fields per workspace here.
              Fields left blank fall through to org branding. Use <strong>Reset to Org Branding</strong> to remove all overrides and restore full inheritance.
            </span>
          </div>

          <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
            <div className="nx-cc-chart-head">
              <span className="nx-cc-chart-title">Workspace White Label</span>
              {wlHasRecord && selectedWsId && (
                <Pill tone="amber">Override active</Pill>
              )}
            </div>
            <div className="nx-form" style={{ marginTop: 14 }}>
              <div className="nx-form-row">
                <label>Workspace</label>
                <select className="nx-input" value={selectedWsId} onChange={e => handleWsChange(e.target.value)}>
                  <option value="">— Select a workspace —</option>
                  {orgWorkspaces.map(ws => <option key={ws.id} value={ws.id}>{ws.name}</option>)}
                </select>
              </div>

              {selectedWsId ? (
                <>
                  <div className="nx-form-row">
                    <label>Display Name</label>
                    <input className="nx-input" value={wlDraft.name} onChange={e => setWlDraft(d => ({ ...d, name: e.target.value }))} placeholder="Leave blank to inherit org name" />
                  </div>
                  <div className="nx-form-row">
                    <label>Logo Override</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input type="file" accept="image/*" className="nx-input" style={{ padding: '4px 8px' }}
                        disabled={wlUploading} onChange={handleWlLogo} />
                      {wlDraft.logoUrl && (
                        <img src={wlDraft.logoUrl} alt="WL Logo preview" style={{ height: 40, maxWidth: 160, objectFit: 'contain', borderRadius: 4, border: '1px solid var(--nx-border)', background: 'var(--nx-bg-alt,#f6f8fa)', padding: 4 }} />
                      )}
                    </div>
                  </div>
                  <div className="nx-form-row">
                    <label>Primary Color</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="color" value={wlDraft.primaryColor || draft.primaryColor || '#125AB0'} onChange={e => setWlDraft(d => ({ ...d, primaryColor: e.target.value }))} className="nx-brand-color-input" />
                      <input className="nx-input" value={wlDraft.primaryColor} onChange={e => setWlDraft(d => ({ ...d, primaryColor: e.target.value }))} placeholder="Leave blank to inherit org color" style={{ flex: 1 }} />
                    </div>
                  </div>
                  <div className="nx-form-row">
                    <label>Secondary Color</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="color" value={wlDraft.secondaryColor || draft.secondary || '#E5F0FF'} onChange={e => setWlDraft(d => ({ ...d, secondaryColor: e.target.value }))} className="nx-brand-color-input" />
                      <input className="nx-input" value={wlDraft.secondaryColor} onChange={e => setWlDraft(d => ({ ...d, secondaryColor: e.target.value }))} placeholder="Leave blank to inherit org color" style={{ flex: 1 }} />
                    </div>
                  </div>
                  <div className="nx-form-row">
                    <label>Welcome Message</label>
                    <input className="nx-input" value={wlDraft.welcomeMessage} onChange={e => setWlDraft(d => ({ ...d, welcomeMessage: e.target.value }))} placeholder="Leave blank to inherit org message" />
                  </div>
                  <div className="nx-form-row">
                    <label>Footer Text</label>
                    <input className="nx-input" value={wlDraft.footerText} onChange={e => setWlDraft(d => ({ ...d, footerText: e.target.value }))} placeholder="Leave blank to inherit org footer" />
                  </div>
                  <div className="nx-form-row">
                    <label>Support Email</label>
                    <input className="nx-input" type="email" value={wlDraft.supportEmail} onChange={e => setWlDraft(d => ({ ...d, supportEmail: e.target.value }))} placeholder="Leave blank to inherit org support email" />
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <Btn variant="primary" icon="Check" onClick={saveWl} disabled={wlSaving}>
                      {wlSaving ? 'Saving…' : 'Save White Label'}
                    </Btn>
                    {wlHasRecord && (
                      <Btn variant="ghost" icon="RotateCcw" onClick={resetWl} disabled={wlResetting}>
                        {wlResetting ? 'Resetting…' : 'Reset to Org Branding'}
                      </Btn>
                    )}
                  </div>
                </>
              ) : (
                <div className="nx-empty" style={{ flexDirection: 'column', gap: 6, padding: '24px 0' }}>
                  <NexusIcon name="Layers" size={22} />
                  <span style={{ fontWeight: 600, fontSize: 13 }}>Select a Workspace</span>
                  <span className="nx-muted nx-mini">Choose a workspace above to configure its white-label settings.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmReset && (
        <TenancyModal title="Reset Branding?" icon="RotateCcw" danger onClose={() => setConfirmReset(false)}>
          <p className="nx-confirm-desc">All custom branding for <strong>{org.name}</strong> will be removed and defaults restored. This cannot be undone.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setConfirmReset(false)}>Cancel</Btn>
            <Btn variant="danger" icon="RotateCcw" onClick={reset}>Reset to Default</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
